import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Modal,
    LinearProgress,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import BudgetEntryBySite from "../../component-files/Forms/BudgetEntrybySite";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    Tooltip as ReTooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import NoteIcon from "@mui/icons-material/Note";

const API_BUDGET = "https://figazuresql.swancreate.com/api/swa/BudgetWorksheet";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_subcategories";

export default function BudgetWorksheetPage() {
    const theme = useTheme();
    const {
        palette: {
            success: { main: successMain },
            error: { main: errorMain },
            primary: { light: primaryLight, main: primaryMain, dark: primaryDark },
            background: { default: bgDefault, paper: bgPaper },
            text: { primary: textPrimary },
        },
    } = theme;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [budgetData, setBudgetData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [subcategoryData, setSubcategoryData] = useState([]);

    const [selectedSite, setSelectedSite] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");

    // Bulk‐update state
    const [updateScopeSubcat, setUpdateScopeSubcat] = useState("");
    const [updatePct, setUpdatePct] = useState("");

    // Progress modal state
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressCount, setProgressCount] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);

    // Chart toggle
    const [chartType, setChartType] = useState("pie");

    // State to control child BudgetEntryBySite modal
    const [entryOpen, setEntryOpen] = useState(false);

    // Note modal state
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [noteRowId, setNoteRowId] = useState(null);

    useEffect(() => {
        loadAllData();
    }, []);

    const loadAllData = async () => {
        setLoading(true);
        await Promise.all([fetchCategories(), fetchSubcategories(), fetchBudgets()]);
        setLoading(false);
    };

    async function fetchCategories() {
        try {
            const res = await fetch(API_CATEGORIES);
            setCategoryData(await res.json());
        } catch {
            setError("Failed to load categories");
        }
    }

    async function fetchSubcategories() {
        try {
            const res = await fetch(API_SUBCATEGORIES);
            setSubcategoryData(await res.json());
        } catch {
            setError("Failed to load subcategories");
        }
    }

    async function fetchBudgets() {
        try {
            const res = await fetch(API_BUDGET);
            const raw = await res.json();
            const mapped = raw.map(r => ({
                ...r,
                Type:
                    r.Type === "R" ? "Income" :
                        r.Type === "E" || r.Type == null ? "Expense" :
                            r.Type,
                Notes: r.Notes || "",
            }));
            setBudgetData(mapped);
        } catch {
            setError("Failed to load budget data");
        }
    }

    const siteOptions = useMemo(() => {
        const map = new Map();
        budgetData.forEach(row => {
            if (!map.has(row.SiteId)) {
                map.set(row.SiteId, row.SiteName);
            }
        });
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [budgetData]);

    // Apply percentage update
    const handleUpdateBudget = () => {
        const pct = parseFloat(updatePct);
        if (isNaN(pct)) {
            alert("Please enter a valid percentage (e.g. 2 or -5)");
            return;
        }
        setBudgetData(prev =>
            prev.map(r => {
                let apply = false;
                if (updateScopeSubcat === "") apply = true;
                else if (updateScopeSubcat === "ALL_INCOME" && r.Type === "Income") apply = true;
                else if (updateScopeSubcat === "ALL_EXPENSE" && r.Type === "Expense") apply = true;
                else if (String(r.SubCategoryId) === updateScopeSubcat) apply = true;
                if (!apply) return r;
                const prevYear = Number(r.PreviousYear) || 0;
                return { ...r, ProposedBudget: prevYear * (1 + pct / 100) };
            })
        );
    };

    // Clear all proposed budgets with confirmation and update database
    const handleClearBudget = async () => {
        if (!window.confirm("Are you sure you want to clear all proposed budgets?")) return;

        setProgressTotal(budgetData.length);
        setProgressCount(0);
        setProgressOpen(true);

        for (let i = 0; i < budgetData.length; i++) {
            const row = budgetData[i];
            const url = `${API_BUDGET}/${row.apiid}`;
            const body = { ProposedBudget: 0 };
            try {
                await fetch(url, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                });
            } catch (err) {
                console.error("Error clearing row", row.apiid, err);
            }
            setProgressCount(i + 1);
        }

        setTimeout(() => setProgressOpen(false), 500);
        // After all rows are updated on the server, clear local state
        setBudgetData(prev =>
            prev.map(r => ({ ...r, ProposedBudget: 0 })));
        alert("All proposed budgets have been cleared.");
    };

    // Save Budget Progress: PUT each row back to API
    const handleFinalizeBudget = async () => {
        setProgressTotal(budgetData.length);
        setProgressCount(0);
        setProgressOpen(true);

        for (let i = 0; i < budgetData.length; i++) {
            const row = budgetData[i];
            const url = `${API_BUDGET}/${row.apiid}`;
            const body = { ProposedBudget: row.ProposedBudget };
            console.log("PUT URL:", url, "BODY:", body);
            try {
                const res = await fetch(url, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                });
                const json = await res.json();
                console.log("RESPONSE STATUS:", res.status, "RESPONSE BODY:", json);
            } catch (err) {
                console.error("Error finalizing row", row.apiid, err);
            }
            setProgressCount(i + 1);
        }

        setTimeout(() => setProgressOpen(false), 500);
        alert("Save Budget Progress complete. See console for details.");
    };

    // Download CSV including specific fields
    const handleDownloadExcel = () => {
        const sheetData = budgetData.map(r => ({
            Fund: r.Fund,
            Func: r.Func,
            Obj: r.Object,
            SiteId: r.SiteId,
            SiteName: r.SiteName,
            CategoryName: r.CategoryName,
            SubCategoryName: r.SubCategoryName,
            ProposedBudget: r.ProposedBudget,
        }));
        const ws = XLSX.utils.json_to_sheet(sheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "BudgetDetails");
        const wbout = XLSX.write(wb, { bookType: "csv", type: "array" });
        saveAs(
            new Blob([wbout], { type: "text/csv;charset=utf-8;" }),
            "Budget_Details.csv"
        );
    };

    // compute total income & expense
    const totals = budgetData.reduce(
        (acc, r) => {
            const pb = Number(r.ProposedBudget) || 0;
            if (r.Type === "Income") acc.income += pb;
            else /* Expense */        acc.expense += pb;
            return acc;
        },
        { income: 0, expense: 0 }
    );

    // breakdown by main category
    const categoryTotals = {};
    budgetData.forEach(r => {
        const pb = Number(r.ProposedBudget) || 0;
        if (!categoryTotals[r.CategoryName]) {
            categoryTotals[r.CategoryName] = { value: pb, type: r.Type };
        } else {
            categoryTotals[r.CategoryName].value += pb;
        }
    });
    const categoryPieData = Object.entries(categoryTotals).map(
        ([name, { value, type }]) => ({ name, value, type })
    );
    const sortedCategoryPieData = categoryPieData.sort(
        (a, b) => (a.type === "Income" ? 0 : 1) - (b.type === "Income" ? 0 : 1)
    );

    // dynamic shades for income/expense
    const successColors = [
        theme.palette.success.light,
        theme.palette.success.main,
        theme.palette.success.dark,
    ];
    const errorColors = [
        theme.palette.error.light,
        theme.palette.error.main,
        theme.palette.error.dark,
    ];

    // Year-by-year data
    const yearKeys = ["PreviousYear", "Year2", "Year3", "Year4"];
    const yearData = yearKeys.map(key => ({
        year: key.replace(/([A-Za-z]+)(\d*)/, (_, label, num) =>
            num ? `Year ${num}` : "Previous Year"
        ),
        Income: budgetData
            .filter(r => r.Type === "Income")
            .reduce((sum, r) => sum + (Number(r[key]) || 0), 0),
        Expense: budgetData
            .filter(r => r.Type === "Expense")
            .reduce((sum, r) => sum + (Number(r[key]) || 0), 0),
    }));

    // Filters
    const filteredRows = budgetData.filter(r =>
        (!selectedSite || String(r.SiteId) === selectedSite) &&
        (!selectedCategory || String(r.CategoryId) === selectedCategory) &&
        (!selectedSubcategory || String(r.SubCategoryId) === selectedSubcategory)
    );

    // Handle click on note icon
    const handleOpenNoteModal = (row) => {
        setNoteRowId(row.apiid);
        setNoteText(row.Notes || "");
        setNoteModalOpen(true);
    };

    const handleCloseNoteModal = () => {
        setNoteModalOpen(false);
        setNoteText("");
        setNoteRowId(null);
    };

    // Save note (POST or PUT via same PUT endpoint)
    const handleSaveNote = async () => {
        if (!noteRowId) {
            handleCloseNoteModal();
            return;
        }
        const url = `${API_BUDGET}/${noteRowId}`;
        const body = { Notes: noteText };
        try {
            const res = await fetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            console.log("Note update response:", res.status, json);
            setBudgetData(prev =>
                prev.map(r =>
                    r.apiid === noteRowId ? { ...r, Notes: noteText } : r
                )
            );
        } catch (err) {
            console.error("Error saving note", noteRowId, err);
        }
        handleCloseNoteModal();
    };

    const columns = [
        { field: "Fund", headerName: "Fund", flex: 0.6 },
        { field: "Func", headerName: "Function", flex: 0.6 },
        { field: "Object", headerName: "Object", flex: 0.6 },
        {
            field: "CategoryName",
            headerName: "Category",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: categoryData.map(c => c.CategoryName),
        },
        {
            field: "SubCategoryName",
            headerName: "Subcategory",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: params =>
                subcategoryData
                    .filter(s => String(s.CategoryID) === String(params.row.CategoryId))
                    .map(s => s.SubcategoryName),
        },
        { field: "SiteName", headerName: "Site", flex: 1 },
        {
            field: "ProposedBudget",
            headerName: "Proposed Budget",
            flex: 1,
            editable: true,
            headerAlign: "center",
            align: "center",
            cellClassName: "proposedBudgetCell",
            renderCell: (params) => {
                const hasNote = params.row.Notes && params.row.Notes.trim() !== "";
                const iconColor = hasNote
                    ? "#1976d2"
                    : theme.palette.mode === "dark"
                        ? "#ffffff"
                        : "#000000";
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                        }}
                    >
                        <Typography>
                            {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(params.value || 0)}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenNoteModal(params.row)}
                            sx={{ marginLeft: 1 }}
                        >
                            <NoteIcon sx={{ color: iconColor }} />
                        </IconButton>
                    </Box>
                );
            },
        },
        {
            field: "YTD",
            headerName: "YTD",
            flex: 1,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        {
            field: "PreviousYear",
            headerName: "Previous Year",
            flex: 1,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        {
            field: "Year2",
            headerName: "Year 2",
            flex: 1,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        {
            field: "Year3",
            headerName: "Year 3",
            flex: 1,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        {
            field: "Year4",
            headerName: "Year 4",
            flex: 1,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        { field: "Type", headerName: "Type", flex: 0.6 },
        { field: "apiid", headerName: "ID", hide: true },
    ];

    return (
        <Box m="20px">
            <Header title="Budget Worksheet" subtitle="Budget prep for current year" />

            {/* PROGRESS MODAL */}
            <Modal open={progressOpen} disableBackdropClick>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 320,
                        bgcolor: primaryLight,
                        borderRadius: 2,
                        p: 3,
                        boxShadow: `0 0 20px rgba(0,0,0,0.5)`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" mb={2}>
                        Processing...
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressTotal ? (progressCount / progressTotal) * 100 : 0}
                        sx={{
                            height: 12,
                            borderRadius: 6,
                            boxShadow: `0 0 8px ${successMain}`,
                            backgroundColor: primaryMain,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: successMain,
                            },
                        }}
                    />
                    <Typography variant="body2" mt={1}>
                        {progressCount} of {progressTotal}
                    </Typography>
                </Box>
            </Modal>

            {/* Note Entry Modal */}
            <Modal open={noteModalOpen} onClose={handleCloseNoteModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: theme.palette.background.paper,
                        borderRadius: 2,
                        p: 3,
                        boxShadow: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography variant="h6" align="center">
                        Enter Note
                    </Typography>
                    <TextField
                        multiline
                        minRows={4}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Enter note here"
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={handleCloseNoteModal} color="inherit">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSaveNote}>
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* STAT CARDS */}
            <Box display="grid" gridTemplateColumns="repeat(4,1fr)" gap={2} mb={4}>
                {yearData.map(({ year, Income, Expense }) => (
                    <Box
                        key={year}
                        p="16px"
                        borderRadius="8px"
                        bgcolor={primaryLight}
                        boxShadow={`0 0 12px rgba(0,0,0,0.1)`}
                    >
                        <Typography variant="h6" mb={1}>{year}</Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="subtitle2">Income</Typography>
                                <Typography
                                    variant="h5"
                                    color={successMain}
                                    sx={{
                                        boxShadow: `0 0 8px ${successMain}`,
                                        borderRadius: "4px",
                                        px: "8px",
                                        py: "4px",
                                        display: "inline-block",
                                    }}
                                >
                                    ${Income.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Expense</Typography>
                                <Typography
                                    variant="h5"
                                    color={errorMain}
                                    sx={{
                                        boxShadow: `0 0 8px ${errorMain}`,
                                        borderRadius: "4px",
                                        px: "8px",
                                        py: "4px",
                                        display: "inline-block",
                                    }}
                                >
                                    ${Expense.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* PIE / BAR TOGGLE & CHARTS */}
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4} mb={3}>
                {/* Left: Toggle + Current Totals + Chart */}
                <Box>
                    {/* Toggle and Totals aligned */}
                    <Box display="flex" alignItems="center" justifyContent="center" gap={4} mb={2}>
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={(e, newType) => { if (newType) setChartType(newType); }}
                        >
                            <ToggleButton value="pie">Pie Chart</ToggleButton>
                            <ToggleButton value="bar">Bar Chart</ToggleButton>
                        </ToggleButtonGroup>
                        <Box display="flex" gap={2}>
                            <Box
                                p="12px"
                                borderRadius="8px"
                                bgcolor={primaryLight}
                                boxShadow={`0 0 12px rgba(0,0,0,0.1)`}
                            >
                                <Typography variant="subtitle2" mb={1}>Total Income</Typography>
                                <Typography
                                    variant="h6"
                                    color={successMain}
                                    sx={{
                                        boxShadow: `0 0 8px ${successMain}`,
                                        borderRadius: "4px",
                                        px: "8px",
                                        py: "4px",
                                        display: "inline-block",
                                    }}
                                >
                                    ${totals.income.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box
                                p="12px"
                                borderRadius="8px"
                                bgcolor={primaryLight}
                                boxShadow={`0 0 12px rgba(0,0,0,0.1)`}
                            >
                                <Typography variant="subtitle2" mb={1}>Total Expense</Typography>
                                <Typography
                                    variant="h6"
                                    color={errorMain}
                                    sx={{
                                        boxShadow: `0 0 8px ${errorMain}`,
                                        borderRadius: "4px",
                                        px: "8px",
                                        py: "4px",
                                        display: "inline-block",
                                    }}
                                >
                                    ${totals.expense.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Chart */}
                    {chartType === "pie" ? (
                        <ResponsiveContainer width="100%" height={500}>
                            <PieChart>
                                <Pie
                                    data={sortedCategoryPieData}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={200}
                                    startAngle={90}
                                    endAngle={-270}
                                    labelLine
                                    label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                                >
                                    {sortedCategoryPieData.map((entry, idx) => (
                                        <Cell
                                            key={`cell-${idx}`}
                                            fill={
                                                entry.type === "Income"
                                                    ? successColors[idx % successColors.length]
                                                    : errorColors[idx % errorColors.length]
                                            }
                                        />
                                    ))}
                                </Pie>
                                <Legend verticalAlign="bottom" />
                                <ReTooltip formatter={v => `$${v.toLocaleString()}`} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                data={sortedCategoryPieData}
                                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                                layout="vertical"
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickFormatter={v => `$${v.toLocaleString()}`}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={150}
                                />
                                <ReTooltip formatter={v => `$${v.toLocaleString()}`} />
                                <Bar dataKey="value">
                                    {sortedCategoryPieData.map((entry, idx) => (
                                        <Cell
                                            key={`cell-${idx}`}
                                            fill={
                                                entry.type === "Income"
                                                    ? successColors[idx % successColors.length]
                                                    : errorColors[idx % errorColors.length]
                                            }
                                        />
                                    ))}
                                </Bar>
                                <Legend verticalAlign="bottom" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Box>

                {/* Right: Income vs Expense by Year */}
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={yearData} margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={v => `$${v.toLocaleString()}`} />
                        <ReTooltip formatter={v => `$${v.toLocaleString()}`} />
                        <Bar dataKey="Income" fill={successMain} />
                        <Bar dataKey="Expense" fill={errorMain} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* FILTERS */}
            <Box display="flex" gap={2} mb={2}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={selectedSite}
                        label="Site"
                        onChange={e => setSelectedSite(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {siteOptions.map(({ id, name }) => (
                            <MenuItem key={id} value={String(id)}>
                                {`${name} (${id})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        label="Category"
                        onChange={e => setSelectedCategory(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {categoryData.map(c => (
                            <MenuItem key={c.CategoryID} value={String(c.CategoryID)}>
                                {c.CategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Subcategory</InputLabel>
                    <Select
                        value={selectedSubcategory}
                        label="Subcategory"
                        onChange={e => setSelectedSubcategory(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {subcategoryData
                            .filter(s =>
                                !selectedCategory ||
                                String(s.CategoryID) === selectedCategory
                            )
                            .map(s => (
                                <MenuItem key={s.SubcategoryID} value={String(s.SubcategoryID)}>
                                    {s.SubcategoryName}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>

            {/* BULK UPDATE, CLEAR, SAVE BUDGET PROGRESS & DOWNLOAD CSV */}
            <Box display="flex" alignItems="center" gap={2} mb={4}>
                <Button variant="contained" color="success" onClick={handleFinalizeBudget}>
                    Save Budget Progress
                </Button>

                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Scope</InputLabel>
                    <Select
                        value={updateScopeSubcat}
                        label="Scope"
                        onChange={e => setUpdateScopeSubcat(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="ALL_INCOME">All Income</MenuItem>
                        <MenuItem value="ALL_EXPENSE">All Expense</MenuItem>
                        {subcategoryData.map(s => (
                            <MenuItem key={s.SubcategoryID} value={String(s.SubcategoryID)}>
                                {s.SubcategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    sx={{ width: 120 }}
                    label="Pct Change"
                    placeholder="e.g. 2 or -5"
                    value={updatePct}
                    onChange={e => setUpdatePct(e.target.value)}
                    InputProps={{ endAdornment: <Typography>%</Typography> }}
                />

                <Button
                    variant="contained"
                    onClick={handleUpdateBudget}
                    sx={{
                        ...(theme.palette.mode === "dark" && {
                            border: "1px solid #ffffff",
                        }),
                    }}
                >
                    Update Budget
                </Button>


                <Button variant="contained" color="error" onClick={handleClearBudget}>
                    Clear Budgets
                </Button>

                <Button variant="contained" color="success" onClick={handleDownloadExcel} sx={{ marginLeft: "auto" }}>
                    Download CSV
                </Button>
            </Box>

            {/* DATA GRID */}
            {!loading && !error && (
                <Box
                    height="65vh"
                    sx={{
                        "& .proposedBudgetCell": {
                            backgroundColor: successMain,
                            color: "#ffffff",
                            fontSize: "1.1rem",
                        },
                        "& .MuiDataGrid-cell.proposedBudgetCell": {
                            justifyContent: "center !important",
                            alignItems: "center",
                        },
                        "& .MuiDataGrid-root": { background: primaryMain },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: primaryLight,
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: primaryDark,
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${primaryLight}`,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            background: primaryLight,
                        },
                    }}
                >
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={r => r.apiid}
                        pageSize={25}
                        rowsPerPageOptions={[10, 25, 50]}
                        disableSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                        processRowUpdate={async (newRow) => {
                            setBudgetData(prev =>
                                prev.map(r => (r.apiid === newRow.apiid ? newRow : r))
                            );
                            return newRow;
                        }}
                    />
                </Box>
            )}

            {error && <Typography color="error">{error}</Typography>}
        </Box>
    );
}
